import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Separator,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Panel,
    PanelType,
    Dropdown,
    IDropdownOption,
    ChoiceGroup,
    IChoiceGroupOption,
    SpinButton,
    Toggle,
    TooltipHost,
    DirectionalHint,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    IColumn,
    Pivot,
    PivotItem,
    Label
} from '@fluentui/react';
import {
    AddRegular,
    DeleteRegular,
    EditRegular,
    DatabaseRegular,
    CheckmarkCircleRegular,
    WarningRegular,
    InfoRegular,
    PlugConnectedRegular,
    TableRegular,
    ArrowSyncRegular,
    BrainCircuitRegular,
    QuestionCircleRegular,
    DocumentRegular,
    ArrowUploadRegular,
    ServerRegular,
    DataUsageRegular,
    LinkRegular,
    CloudDatabaseRegular,
    DocumentTableRegular,
    SparkleRegular,
    FlowRegular,
    SettingsRegular,
    EyeRegular,
    EyeOffRegular,
    PlugConnectedSettingsRegular,
    DatabaseLinkRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import SemanticMappingEditor from './SemanticMappingEditor';
import ExampleQuestionsEditor from './ExampleQuestionsEditor';
import RelationshipsEditor from './RelationshipsEditor';

// Types
interface StructuredSource {
    id: string;
    name: string;
    source_type: string;
    description?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
    created_by?: string;
    server?: string;
    database?: string;
    tables_count: number;
    questions_count: number;
    relationships_count: number;
}

interface ConnectionConfig {
    server: string;
    database: string;
    port: number;
    username?: string;
    auth_type: string;
}

interface TableReference {
    name: string;
    schema?: string;
    table_type?: string;
    row_count?: number;
}

interface ColumnDefinition {
    name: string;
    data_type: string;
    is_nullable: boolean;
    is_primary_key?: boolean;
}

interface SourceType {
    type: string;
    name: string;
    capabilities: {
        supports_views: boolean;
        supports_stored_procedures: boolean;
        supports_transactions: boolean;
        supports_relationships: boolean;
        supports_streaming: boolean;
        max_rows_per_query: number;
        requires_upload: boolean;
    };
}

const stackTokens: IStackTokens = { childrenGap: 15 };

// Info tooltip component
interface FieldInfoProps {
    title: string;
    description: string;
}

const FieldInfo: React.FC<FieldInfoProps> = ({ title, description }) => (
    <TooltipHost
        content={
            <div style={{ padding: '8px', maxWidth: '300px' }}>
                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, display: 'block', marginBottom: '4px' }}>
                    {title}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>
                    {description}
                </Text>
            </div>
        }
        directionalHint={DirectionalHint.topCenter}
        styles={{ root: { display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'help' } }}
    >
        <InfoRegular style={{ fontSize: '14px', color: '#667eea' }} />
    </TooltipHost>
);

// Modern app-consistent color palette
const COLORS = {
    primary: '#667eea',
    primaryLight: '#818cf8',
    primaryDark: '#4f46e5',
    success: '#10b981',
    successLight: '#d1fae5',
    warning: '#f59e0b',
    warningLight: '#fef3c7',
    danger: '#ef4444',
    dangerLight: '#fee2e2',
    info: '#0ea5e9',
    infoLight: '#e0f2fe',
    purple: '#8b5cf6',
    purpleLight: '#ede9fe',
    gray: '#6b7280',
    grayLight: '#f3f4f6',
    cardBg: '#ffffff',
    pageBg: '#f8fafc',
    border: '#e2e8f0',
    borderHover: '#cbd5e1'
};

// Styles
const styles = mergeStyleSets({
    container: {
        padding: '0',
        backgroundColor: COLORS.pageBg,
        minHeight: '100vh'
    },
    heroSection: {
        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
        padding: '32px 24px',
        borderRadius: '0 0 24px 24px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
    },
    heroTitle: {
        fontSize: '28px',
        fontWeight: FontWeights.bold,
        color: 'white',
        margin: '0'
    },
    heroSubtitle: {
        fontSize: '14px',
        color: 'rgba(255,255,255,0.85)',
        marginTop: '8px'
    },
    heroActions: {
        marginTop: '16px'
    },
    contentArea: {
        padding: '0 24px 24px 24px'
    },
    sourceCard: {
        padding: '20px',
        border: `1px solid ${COLORS.border}`,
        borderRadius: '16px',
        backgroundColor: COLORS.cardBg,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'relative',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        ':hover': {
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
            transform: 'translateY(-4px)',
            borderColor: COLORS.primaryLight
        }
    },
    sourceDisabled: {
        opacity: 0.7,
        backgroundColor: COLORS.grayLight,
        ':hover': {
            transform: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }
    },
    sourceAccent: {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        height: '4px',
        background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.purple})`
    },
    sourceHeader: {
        paddingBottom: '16px',
        marginBottom: '16px',
        borderBottom: `1px solid ${COLORS.border}`
    },
    sourceName: {
        fontSize: '18px',
        fontWeight: FontWeights.semibold,
        color: '#1e293b'
    },
    sourceType: {
        fontSize: '12px',
        color: COLORS.gray,
        fontWeight: FontWeights.semibold,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px'
    },
    statusBadge: {
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: FontWeights.semibold,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    activeBadge: {
        backgroundColor: COLORS.successLight,
        color: '#047857'
    },
    inactiveBadge: {
        backgroundColor: COLORS.dangerLight,
        color: '#dc2626'
    },
    sourcesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
        gap: '20px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
    },
    statCard: {
        padding: '20px',
        backgroundColor: COLORS.cardBg,
        borderRadius: '16px',
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        transition: 'all 0.2s ease',
        ':hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }
    },
    statIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    statNumber: {
        fontSize: '28px',
        fontWeight: FontWeights.bold,
        lineHeight: '1'
    },
    statLabel: {
        fontSize: '13px',
        color: COLORS.gray,
        marginTop: '4px'
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '16px',
        marginTop: '12px',
        padding: '12px',
        backgroundColor: COLORS.grayLight,
        borderRadius: '10px'
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        color: '#475569'
    },
    typeIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: `linear-gradient(135deg, ${COLORS.primary}15 0%, ${COLORS.purple}15 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    actionBar: {
        display: 'flex',
        gap: '8px',
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: `1px solid ${COLORS.border}`
    },
    actionButton: {
        flex: 1,
        borderRadius: '10px',
        height: '38px',
        fontSize: '13px',
        fontWeight: FontWeights.semibold
    },
    iconButton: {
        borderRadius: '10px',
        width: '38px',
        height: '38px',
        border: `1px solid ${COLORS.border}`
    },
    emptyState: {
        padding: '60px 40px',
        backgroundColor: COLORS.cardBg,
        borderRadius: '20px',
        border: `2px dashed ${COLORS.border}`,
        textAlign: 'center' as const
    },
    emptyIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: `linear-gradient(135deg, ${COLORS.primary}10 0%, ${COLORS.purple}10 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px'
    },
    formSection: {
        padding: '20px',
        backgroundColor: COLORS.grayLight,
        borderRadius: '12px',
        marginBottom: '16px'
    },
    formSectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '15px',
        fontWeight: FontWeights.semibold,
        color: '#1e293b',
        marginBottom: '16px'
    },
    uploadZone: {
        border: `2px dashed ${COLORS.border}`,
        borderRadius: '16px',
        padding: '40px',
        textAlign: 'center' as const,
        backgroundColor: COLORS.cardBg,
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    uploadZoneActive: {
        borderColor: COLORS.primary,
        backgroundColor: `${COLORS.primary}08`
    },
    connectionIndicator: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0
    }
});

// =============================================================================
// Files Manager Component (for multi-file Excel sources)
// =============================================================================

interface FileInfo {
    id: string;
    filename: string;
    sheets: string[];
    file_size?: number;
    uploaded_at?: string;
    is_legacy?: boolean;
}

interface FilesManagerProps {
    sourceId: string;
    onFilesChanged?: () => void;
}

const FilesManager: React.FC<FilesManagerProps> = ({ sourceId, onFilesChanged }) => {
    const [files, setFiles] = useState<FileInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    const fetchFiles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/files`);
            if (response.ok) {
                const data = await response.json();
                setFiles(data.files || []);
            } else {
                setError('Failed to load files');
            }
        } catch (err) {
            setError('Error loading files');
        } finally {
            setLoading(false);
        }
    }, [sourceId]);
    
    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);
    
    const handleAddFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setUploading(true);
        setError(null);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/add-file`, {
                method: 'POST',
                body: formData
            });
            
            if (response.ok) {
                const data = await response.json();
                setSuccess(`Added ${data.filename} with ${data.sheets.length} sheet(s). Total: ${data.total_files} files, ${data.total_sheets} sheets.`);
                fetchFiles();
                onFilesChanged?.();
                setTimeout(() => setSuccess(null), 5000);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to add file');
            }
        } catch (err) {
            setError('Error adding file');
        } finally {
            setUploading(false);
            // Reset file input
            event.target.value = '';
        }
    };
    
    const handleRemoveFile = async (fileId: string, filename: string) => {
        if (!confirm(`Remove "${filename}" from this source?`)) return;
        
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/files/${fileId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                setSuccess('File removed');
                fetchFiles();
                onFilesChanged?.();
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError('Failed to remove file');
            }
        } catch (err) {
            setError('Error removing file');
        }
    };
    
    if (loading) {
        return (
            <Stack horizontalAlign="center" styles={{ root: { padding: '40px' } }}>
                <Spinner size={SpinnerSize.large} label="Loading files..." />
            </Stack>
        );
    }
    
    return (
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: '16px' } }}>
            {error && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
                    {error}
                </MessageBar>
            )}
            {success && (
                <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess(null)}>
                    {success}
                </MessageBar>
            )}
            
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                    📁 Excel Files ({files.length})
                </Text>
                <div>
                    <input
                        type="file"
                        accept=".xlsx,.xls,.xlsm,.csv,.tsv"
                        onChange={handleAddFile}
                        style={{ display: 'none' }}
                        id="add-file-input"
                        disabled={uploading}
                    />
                    <label htmlFor="add-file-input">
                        <PrimaryButton
                            text={uploading ? "Uploading..." : "Add File"}
                            iconProps={{ iconName: 'Add' }}
                            disabled={uploading}
                            onClick={() => document.getElementById('add-file-input')?.click()}
                        />
                    </label>
                </div>
            </Stack>
            
            <Text variant="small" styles={{ root: { color: '#666' } }}>
                Add multiple Excel files to combine their sheets into a single unified dataset. 
                Each sheet will appear in the Tables & Mappings tab.
            </Text>
            
            {files.length === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    No files uploaded yet. Add an Excel file to get started.
                </MessageBar>
            ) : (
                <Stack tokens={{ childrenGap: 8 }}>
                    {files.map((file) => (
                        <Stack 
                            key={file.id}
                            horizontal 
                            tokens={{ childrenGap: 12 }}
                            verticalAlign="center"
                            styles={{ 
                                root: { 
                                    padding: '12px 16px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e1e1e1'
                                } 
                            }}
                        >
                            <DocumentRegular style={{ fontSize: '24px', color: '#217346' }} />
                            <Stack styles={{ root: { flex: 1 } }}>
                                <Text styles={{ root: { fontWeight: 600 } }}>
                                    {file.filename}
                                    {file.is_legacy && <span style={{ color: '#888', fontSize: '11px', marginLeft: '8px' }}>(original)</span>}
                                </Text>
                                <Text variant="small" styles={{ root: { color: '#666' } }}>
                                    {file.sheets.length} sheet(s): {file.sheets.join(', ')}
                                </Text>
                                {file.uploaded_at && (
                                    <Text variant="xSmall" styles={{ root: { color: '#999' } }}>
                                        Uploaded: {new Date(file.uploaded_at).toLocaleDateString()}
                                    </Text>
                                )}
                            </Stack>
                            {!file.is_legacy && (
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title="Remove file"
                                    onClick={() => handleRemoveFile(file.id, file.filename)}
                                    styles={{ root: { color: '#d13438' } }}
                                />
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

// Inject CSS animations for spin effect
const injectAnimations = () => {
    if (!document.getElementById('structured-sources-animations')) {
        const style = document.createElement('style');
        style.id = 'structured-sources-animations';
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }
};

export const StructuredDataSources: React.FC = () => {
    // Inject animations on mount
    useEffect(() => {
        injectAnimations();
    }, []);
    
    // State
    const [sources, setSources] = useState<StructuredSource[]>([]);
    const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [editingSource, setEditingSource] = useState<StructuredSource | null>(null);
    const [isMappingPanelOpen, setIsMappingPanelOpen] = useState(false);
    const [selectedSourceForMapping, setSelectedSourceForMapping] = useState<StructuredSource | null>(null);
    
    // Panel mode: 'sql' | 'file'
    const [panelMode, setPanelMode] = useState<'sql' | 'file'>('sql');
    
    // File upload state - supports multiple files
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        source_type: 'azure_sql',
        server: '',
        database: '',
        port: 1433,
        username: '',
        password: '',
        auth_type: 'sql_auth',
        description: '',
        // Service Principal fields
        tenant_id: '',
        client_id: '',
        client_secret: ''
    });
    
    // Connection test state
    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionTestResult, setConnectionTestResult] = useState<{ success: boolean; message?: string } | null>(null);
    
    // Table discovery state
    const [tables, setTables] = useState<TableReference[]>([]);
    const [loadingTables, setLoadingTables] = useState(false);
    
    // Re-analyze state
    const [reanalyzingSourceId, setReanalyzingSourceId] = useState<string | null>(null);
    
    // Fetch sources
    const fetchSources = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch('/api/admin/structured-sources');
            if (response.ok) {
                const data = await response.json();
                setSources(data.connections || []);
            } else {
                setError('Failed to load structured data sources');
            }
        } catch (err) {
            setError('Error loading data sources');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);
    
    // Fetch source types
    const fetchSourceTypes = useCallback(async () => {
        try {
            const response = await authenticatedFetch('/api/admin/structured-sources/types');
            if (response.ok) {
                const data = await response.json();
                setSourceTypes(data.types || []);
            }
        } catch (err) {
            console.error('Error loading source types:', err);
        }
    }, []);
    
    useEffect(() => {
        fetchSources();
        fetchSourceTypes();
    }, [fetchSources, fetchSourceTypes]);
    
    // Open panel for creating new source
    const handleAddNew = () => {
        setEditingSource(null);
        setPanelMode('sql');
        setSelectedFiles([]);
        setUploadProgress(0);
        setFormData({
            name: '',
            source_type: 'azure_sql',
            server: '',
            database: '',
            port: 1433,
            username: '',
            password: '',
            auth_type: 'sql_auth',
            description: '',
            tenant_id: '',
            client_id: '',
            client_secret: ''
        });
        setConnectionTestResult(null);
        setIsPanelOpen(true);
    };
    
    // File upload handlers
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            validateAndAddFiles(files);
        }
        // Reset input so same file can be selected again if removed
        e.target.value = '';
    };
    
    const validateAndAddFiles = (files: FileList | File[]) => {
        const allowedExtensions = ['.xlsx', '.xls', '.csv', '.tsv'];
        const validFiles: File[] = [];
        
        for (const file of Array.from(files)) {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            
            if (!allowedExtensions.includes(ext)) {
                setError(`Unsupported file type: ${file.name}. Allowed: ${allowedExtensions.join(', ')}`);
                continue;
            }
            
            if (file.size > 100 * 1024 * 1024) { // 100MB limit per file
                setError(`File ${file.name} exceeds 100MB limit`);
                continue;
            }
            
            // Check for duplicates
            if (!selectedFiles.some(f => f.name === file.name)) {
                validFiles.push(file);
            }
        }
        
        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            // Auto-fill name from first file if empty
            if (!formData.name && (selectedFiles.length === 0)) {
                const nameWithoutExt = validFiles[0].name.replace(/\.[^/.]+$/, '');
                setFormData(prev => ({ ...prev, name: nameWithoutExt }));
            }
        }
    };
    
    const removeSelectedFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };
    
    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };
    
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            validateAndAddFiles(files);
        }
    };
    
    const handleFileUpload = async () => {
        if (selectedFiles.length === 0 || !formData.name) {
            setError('Please select at least one file and provide a name');
            return;
        }
        
        setIsUploading(true);
        setUploadProgress(0);
        
        try {
            const formDataObj = new FormData();
            // Append all files with 'files' key (backend expects multiple)
            selectedFiles.forEach(file => {
                formDataObj.append('files', file);
            });
            formDataObj.append('name', formData.name);
            formDataObj.append('description', formData.description);
            
            // Use multi-file endpoint if more than one file
            const endpoint = selectedFiles.length > 1 
                ? '/api/admin/structured-sources/upload-multiple'
                : '/api/admin/structured-sources/upload';
            
            // For single file, use 'file' key instead
            if (selectedFiles.length === 1) {
                formDataObj.delete('files');
                formDataObj.append('file', selectedFiles[0]);
                formDataObj.append('source_type', selectedFiles[0].name.toLowerCase().endsWith('.csv') ? 'csv' : 'excel');
            }
            
            const response = await authenticatedFetch(endpoint, {
                method: 'POST',
                body: formDataObj
            });
            
            setUploadProgress(100);
            
            if (response.ok) {
                const data = await response.json();
                const filesCount = data.files?.length || 1;
                const totalRows = data.total_rows || data.rows_detected || 0;
                const totalSheets = data.total_sheets || data.sheets?.length || 0;
                setSuccess(
                    filesCount > 1 
                        ? `Successfully uploaded ${filesCount} files with ${totalSheets} sheets. ${totalRows} total rows detected.`
                        : `File uploaded successfully. ${totalRows} rows detected.`
                );
                handleClosePanel();
                fetchSources();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to upload files');
            }
        } catch (err) {
            setError('Error uploading files');
            console.error(err);
        } finally {
            setIsUploading(false);
        }
    };
    
    // Open panel for editing
    const handleEdit = async (source: StructuredSource) => {
        setEditingSource(source);
        
        // Set panel mode based on source type
        const isFileSource = source.source_type === 'excel' || source.source_type === 'csv';
        setPanelMode(isFileSource ? 'file' : 'sql');
        
        // Fetch full config
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${source.id}`);
            if (response.ok) {
                const data = await response.json();
                setFormData({
                    name: data.name || '',
                    source_type: data.source_type || 'azure_sql',
                    server: data.connection?.server || '',
                    database: data.connection?.database || '',
                    port: data.connection?.port || 1433,
                    username: data.connection?.username || '',
                    password: '', // Don't show existing password
                    auth_type: data.connection?.auth_type || 'sql_auth',
                    description: data.description || '',
                    tenant_id: data.connection?.tenant_id || '',
                    client_id: data.connection?.client_id || '',
                    client_secret: '' // Don't show existing secret
                });
            }
        } catch (err) {
            setError('Error loading source details');
        }
        setConnectionTestResult(null);
        setIsPanelOpen(true);
    };
    
    // Test connection
    const handleTestConnection = async () => {
        setTestingConnection(true);
        setConnectionTestResult(null);
        try {
            // If editing, test existing connection
            if (editingSource) {
                const response = await authenticatedFetch(`/api/admin/structured-sources/${editingSource.id}/test`, {
                    method: 'POST'
                });
                const result = await response.json();
                setConnectionTestResult({
                    success: result.success,
                    message: result.success ? 'Connection successful' : result.error || 'Connection failed'
                });
            } else {
                // For new connection, we need to create first then test
                setConnectionTestResult({
                    success: false,
                    message: 'Save the connection first to test it'
                });
            }
        } catch (err) {
            setConnectionTestResult({ success: false, message: 'Error testing connection' });
        } finally {
            setTestingConnection(false);
        }
    };
    
    // Save source
    const handleSave = async () => {
        try {
            const url = editingSource 
                ? `/api/admin/structured-sources/${editingSource.id}`
                : '/api/admin/structured-sources';
            
            // Build payload - only include secrets if they have values
            // This prevents sending empty strings that could overwrite existing secrets
            const payload: Record<string, unknown> = {
                name: formData.name,
                source_type: formData.source_type,
                server: formData.server,
                database: formData.database,
                port: formData.port,
                auth_type: formData.auth_type,
                description: formData.description
            };
            
            // Only include credentials if provided (never send empty secrets)
            if (formData.auth_type === 'sql_auth') {
                payload.username = formData.username;
                if (formData.password) {
                    payload.password = formData.password;
                }
            } else if (formData.auth_type === 'service_principal') {
                payload.tenant_id = formData.tenant_id;
                payload.client_id = formData.client_id;
                if (formData.client_secret) {
                    payload.client_secret = formData.client_secret;
                }
            }
            
            const response = await authenticatedFetch(url, {
                method: editingSource ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (response.ok) {
                setSuccess(editingSource ? 'Source updated successfully' : 'Source created successfully');
                handleClosePanel();  // Clears sensitive data
                fetchSources();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to save source');
            }
        } catch (err) {
            setError('Error saving source');
        }
    };
    
    // Delete source
    const handleDelete = async (source: StructuredSource) => {
        if (!window.confirm(`Delete "${source.name}"? This will remove all semantic mappings and example questions.`)) {
            return;
        }
        
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${source.id}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                setSuccess('Source deleted successfully');
                fetchSources();
            } else {
                setError('Failed to delete source');
            }
        } catch (err) {
            setError('Error deleting source');
        }
    };
    
    // Open mapping panel
    const handleOpenMapping = (source: StructuredSource) => {
        setSelectedSourceForMapping(source);
        setIsMappingPanelOpen(true);
    };
    
    // Close panel and clear sensitive data from memory
    const handleClosePanel = () => {
        setIsPanelOpen(false);
        // Clear sensitive data immediately to prevent exposure in memory/devtools
        setFormData(prev => ({
            ...prev,
            password: '',
            client_secret: ''
        }));
    };
    
    // Re-analyze source
    const handleReanalyze = async (source: StructuredSource) => {
        // Confirmation to prevent accidental clicks
        if (!window.confirm(`Re-run AI semantic analysis for "${source.name}"?\n\nThis will overwrite existing semantic mappings with new AI-generated suggestions.`)) {
            return;
        }
        
        setReanalyzingSourceId(source.id);
        setError(null);
        
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${source.id}/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}) // Analyze all tables
            });
            
            if (response.ok) {
                const data = await response.json();
                const analyzedCount = Object.keys(data).filter(k => k !== 'error' && k !== 'message').length;
                setSuccess(`Re-analyzed ${analyzedCount} table(s) for "${source.name}"`);
                setTimeout(() => setSuccess(null), 5000);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to re-analyze source');
            }
        } catch (err) {
            setError('Error re-analyzing source');
        } finally {
            setReanalyzingSourceId(null);
        }
    };
    
    // Discover tables
    const handleDiscoverTables = async (sourceId: string) => {
        setLoadingTables(true);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/tables`);
            if (response.ok) {
                const data = await response.json();
                setTables(data.tables || []);
            }
        } catch (err) {
            setError('Error discovering tables');
        } finally {
            setLoadingTables(false);
        }
    };
    
    // Source type options - filter to show only SQL types in database dropdown
    const sqlOnlyTypes = ['sql_azure', 'sql_server', 'azure_sql'];
    const sourceTypeOptions: IDropdownOption[] = sourceTypes
        .filter(t => sqlOnlyTypes.includes(t.type))
        .map(t => ({
            key: t.type,
            text: t.name
        }));
    
    // Auth type options
    const authTypeOptions: IChoiceGroupOption[] = [
        { key: 'sql_auth', text: 'SQL Authentication', iconProps: { iconName: 'PermissionsSolid' } },
        { key: 'managed_identity', text: 'Managed Identity (Azure)', iconProps: { iconName: 'AzureLogo' } },
        { key: 'service_principal', text: 'Service Principal (App Registration)', iconProps: { iconName: 'AzureKeyVault' } }
    ];
    
    // Render source card
    const renderSourceCard = (source: StructuredSource) => {
        const isExcel = source.source_type === 'excel' || source.source_type === 'csv';
        const SourceIcon = isExcel ? DocumentTableRegular : CloudDatabaseRegular;
        
        return (
            <div 
                key={source.id}
                className={`${styles.sourceCard} ${!source.is_active ? styles.sourceDisabled : ''}`}
            >
                {/* Accent bar at top */}
                <div className={styles.sourceAccent} />
                
                <Stack horizontal horizontalAlign="space-between" className={styles.sourceHeader} style={{ paddingTop: '8px' }}>
                    <Stack horizontal tokens={{ childrenGap: 14 }} verticalAlign="center">
                        <div className={styles.typeIcon}>
                            <SourceIcon style={{ fontSize: '24px', color: COLORS.primary }} />
                        </div>
                        <Stack>
                            <Text className={styles.sourceName}>{source.name}</Text>
                            <Text className={styles.sourceType}>
                                {source.source_type.replace('_', ' ')}
                            </Text>
                        </Stack>
                    </Stack>
                    <span className={`${styles.statusBadge} ${source.is_active ? styles.activeBadge : styles.inactiveBadge}`}>
                        <div 
                            className={styles.connectionIndicator} 
                            style={{ backgroundColor: source.is_active ? COLORS.success : COLORS.danger }}
                        />
                        {source.is_active ? 'Active' : 'Inactive'}
                    </span>
                </Stack>
                
                {/* Connection info */}
                {(source.server || source.database) && (
                    <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginBottom: '12px' } }}>
                        {source.server && (
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                <ServerRegular style={{ fontSize: '14px', color: COLORS.gray }} />
                                <Text variant="small" style={{ color: '#475569' }}>
                                    {source.server}
                                </Text>
                            </Stack>
                        )}
                        {source.database && (
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                <DatabaseRegular style={{ fontSize: '14px', color: COLORS.gray }} />
                                <Text variant="small" style={{ color: '#475569' }}>
                                    {source.database}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}
                
                {/* Stats row */}
                <div className={styles.metaRow}>
                    <div className={styles.metaItem}>
                        <TableRegular style={{ color: COLORS.primary }} />
                        <span>{source.tables_count} tables</span>
                    </div>
                    <div className={styles.metaItem}>
                        <QuestionCircleRegular style={{ color: COLORS.purple }} />
                        <span>{source.questions_count} questions</span>
                    </div>
                    <div className={styles.metaItem}>
                        <FlowRegular style={{ color: COLORS.info }} />
                        <span>{source.relationships_count} relations</span>
                    </div>
                </div>
                
                {/* Action buttons */}
                <div className={styles.actionBar}>
                    <PrimaryButton 
                        className={styles.actionButton}
                        styles={{ 
                            root: { 
                                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                                border: 'none'
                            } 
                        }}
                        onClick={() => handleOpenMapping(source)}
                    >
                        <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                            <SparkleRegular style={{ fontSize: '16px' }} />
                            <span>AI Mapping</span>
                        </Stack>
                    </PrimaryButton>
                    <TooltipHost content="Re-run AI semantic analysis" directionalHint={DirectionalHint.topCenter}>
                        <IconButton 
                            className={styles.iconButton}
                            iconProps={{ iconName: 'Sync' }}
                            title="Re-analyze"
                            onClick={() => handleReanalyze(source)}
                            disabled={reanalyzingSourceId === source.id}
                            styles={{ 
                                root: {
                                    animation: reanalyzingSourceId === source.id ? 'spin 1s linear infinite' : 'none',
                                },
                                icon: { color: COLORS.primary } 
                            }}
                        />
                    </TooltipHost>
                    <IconButton 
                        className={styles.iconButton}
                        iconProps={{ iconName: 'Edit' }}
                        title="Edit settings"
                        onClick={() => handleEdit(source)}
                        styles={{ icon: { color: COLORS.gray } }}
                    />
                    <IconButton 
                        className={styles.iconButton}
                        iconProps={{ iconName: 'Delete' }}
                        title="Delete"
                        onClick={() => handleDelete(source)}
                        styles={{ 
                            root: { border: `1px solid ${COLORS.dangerLight}` },
                            icon: { color: COLORS.danger }
                        }}
                    />
                </div>
            </div>
        );
    };
    
    if (loading) {
        return (
            <div className={styles.container}>
                <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: '80px', minHeight: '400px' } }}>
                    <Spinner size={SpinnerSize.large} />
                    <Text variant="large" style={{ marginTop: '20px', color: COLORS.gray }}>
                        Loading structured data sources...
                    </Text>
                </Stack>
            </div>
        );
    }
    
    return (
        <div className={styles.container}>
            {/* Hero Section */}
            <div className={styles.heroSection}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                        <div style={{ 
                            width: '56px', 
                            height: '56px', 
                            borderRadius: '14px', 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <DatabaseLinkRegular style={{ fontSize: '28px', color: 'white' }} />
                        </div>
                        <div>
                            <h1 className={styles.heroTitle}>Structured Data Sources</h1>
                            <Text className={styles.heroSubtitle}>
                                Connect SQL databases or upload Excel/CSV files with AI-powered semantic mappings
                            </Text>
                        </div>
                    </Stack>
                    <div className={styles.heroActions}>
                        <PrimaryButton 
                            styles={{ 
                                root: { 
                                    backgroundColor: 'white', 
                                    color: COLORS.primary,
                                    borderRadius: '10px',
                                    height: '42px',
                                    padding: '0 24px',
                                    border: 'none',
                                    fontWeight: FontWeights.semibold
                                },
                                rootHovered: {
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    color: COLORS.primaryDark
                                }
                            }}
                            onClick={handleAddNew}
                        >
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                <AddRegular style={{ fontSize: '18px' }} />
                                <span>Add Data Source</span>
                            </Stack>
                        </PrimaryButton>
                    </div>
                </Stack>
            </div>
            
            {/* Content Area */}
            <div className={styles.contentArea}>
                {/* Messages */}
                {error && (
                    <MessageBar 
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        styles={{ root: { marginBottom: '16px', borderRadius: '8px' } }}
                    >
                        {error}
                    </MessageBar>
                )}
                
                {success && (
                    <MessageBar 
                        messageBarType={MessageBarType.success}
                        onDismiss={() => setSuccess(null)}
                        styles={{ root: { marginBottom: '16px', borderRadius: '8px' } }}
                    >
                        {success}
                    </MessageBar>
                )}
                
                {/* Stats Grid */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon} style={{ backgroundColor: `${COLORS.primary}15` }}>
                            <DatabaseRegular style={{ fontSize: '24px', color: COLORS.primary }} />
                        </div>
                        <div>
                            <Text className={styles.statNumber} style={{ color: COLORS.primary }}>
                                {sources.length}
                            </Text>
                            <Text className={styles.statLabel}>Total Sources</Text>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon} style={{ backgroundColor: COLORS.successLight }}>
                            <CheckmarkCircleRegular style={{ fontSize: '24px', color: COLORS.success }} />
                        </div>
                        <div>
                            <Text className={styles.statNumber} style={{ color: COLORS.success }}>
                                {sources.filter(s => s.is_active).length}
                            </Text>
                            <Text className={styles.statLabel}>Active</Text>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon} style={{ backgroundColor: COLORS.infoLight }}>
                            <TableRegular style={{ fontSize: '24px', color: COLORS.info }} />
                        </div>
                        <div>
                            <Text className={styles.statNumber} style={{ color: COLORS.info }}>
                                {sources.reduce((sum, s) => sum + s.tables_count, 0)}
                            </Text>
                            <Text className={styles.statLabel}>Tables Mapped</Text>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon} style={{ backgroundColor: COLORS.purpleLight }}>
                            <QuestionCircleRegular style={{ fontSize: '24px', color: COLORS.purple }} />
                        </div>
                        <div>
                            <Text className={styles.statNumber} style={{ color: COLORS.purple }}>
                                {sources.reduce((sum, s) => sum + s.questions_count, 0)}
                            </Text>
                            <Text className={styles.statLabel}>Example Questions</Text>
                        </div>
                    </div>
                </div>
                
                {/* Sources Grid */}
                {sources.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>
                            <DatabaseRegular style={{ fontSize: '40px', color: COLORS.primary }} />
                        </div>
                        <Text variant="xLarge" style={{ color: '#1e293b', fontWeight: FontWeights.semibold }}>
                            No data sources configured
                        </Text>
                        <Text variant="medium" style={{ color: COLORS.gray, marginTop: '8px', display: 'block' }}>
                            Add a SQL connection or upload an Excel/CSV file to enable AI-powered data analysis
                        </Text>
                        <PrimaryButton 
                            styles={{ 
                                root: { 
                                    marginTop: '24px',
                                    borderRadius: '10px',
                                    height: '44px',
                                    padding: '0 28px',
                                    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                                    border: 'none'
                                } 
                            }}
                            onClick={handleAddNew}
                        >
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                <AddRegular style={{ fontSize: '18px' }} />
                                <span>Add Your First Data Source</span>
                            </Stack>
                        </PrimaryButton>
                    </div>
                ) : (
                    <div className={styles.sourcesGrid}>
                        {sources.map(renderSourceCard)}
                    </div>
                )}
            </div>
            
            {/* Create/Edit Panel */}
            <Panel
                isOpen={isPanelOpen}
                onDismiss={handleClosePanel}
                type={PanelType.medium}
                headerText={editingSource ? 'Edit Data Source' : 'Add Structured Data Source'}
                closeButtonAriaLabel="Close"
                styles={{
                    main: { backgroundColor: COLORS.pageBg },
                    content: { padding: '0' },
                    commands: { marginTop: '0' }
                }}
            >
                <Stack tokens={stackTokens} styles={{ root: { padding: '24px' } }}>
                    {/* Mode selector - only for new sources */}
                    {!editingSource && (
                        <>
                            <div className={styles.formSection}>
                                <div className={styles.formSectionTitle}>
                                    <DataUsageRegular style={{ fontSize: '20px', color: COLORS.primary }} />
                                    <span>Source Type</span>
                                </div>
                                <ChoiceGroup
                                    selectedKey={panelMode}
                                    options={[
                                        { 
                                            key: 'sql', 
                                            text: 'SQL Database Connection',
                                            iconProps: { iconName: 'Database' },
                                            styles: { 
                                                root: { marginBottom: '12px' },
                                                choiceFieldWrapper: { 
                                                    padding: '12px',
                                                    backgroundColor: panelMode === 'sql' ? `${COLORS.primary}10` : 'transparent',
                                                    borderRadius: '8px',
                                                    border: panelMode === 'sql' ? `2px solid ${COLORS.primary}` : '2px solid transparent'
                                                }
                                            }
                                        },
                                        { 
                                            key: 'file', 
                                            text: 'Upload Excel/CSV File',
                                            iconProps: { iconName: 'ExcelDocument' },
                                            styles: { 
                                                choiceFieldWrapper: { 
                                                    padding: '12px',
                                                    backgroundColor: panelMode === 'file' ? `${COLORS.primary}10` : 'transparent',
                                                    borderRadius: '8px',
                                                    border: panelMode === 'file' ? `2px solid ${COLORS.primary}` : '2px solid transparent'
                                                }
                                            }
                                        }
                                    ]}
                                    onChange={(_, option) => {
                                        setPanelMode(option?.key as 'sql' | 'file' || 'sql');
                                        setSelectedFiles([]);
                                    }}
                                />
                            </div>
                        </>
                    )}
                    
                    <div className={styles.formSection}>
                        <div className={styles.formSectionTitle}>
                            <InfoRegular style={{ fontSize: '20px', color: COLORS.info }} />
                            <span>Basic Information</span>
                        </div>
                        <TextField
                            label="Data Source Name"
                            required
                            value={formData.name}
                            onChange={(_, val) => setFormData({ ...formData, name: val || '' })}
                            placeholder={panelMode === 'sql' ? 'e.g., Sales Database' : 'e.g., Q1 Sales Report'}
                            styles={{ root: { marginBottom: '12px' } }}
                        />
                        
                        <TextField
                            label="Description"
                            multiline
                            rows={2}
                            value={formData.description}
                            onChange={(_, val) => setFormData({ ...formData, description: val || '' })}
                            placeholder="Brief description of this data source"
                        />
                    </div>
                    
                    {/* File Upload Mode */}
                    {panelMode === 'file' && !editingSource && (
                        <div className={styles.formSection}>
                            <div className={styles.formSectionTitle}>
                                <ArrowUploadRegular style={{ fontSize: '20px', color: COLORS.purple }} />
                                <span>Upload File</span>
                            </div>
                            
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`${styles.uploadZone} ${isDragging ? styles.uploadZoneActive : ''}`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.xlsm,.csv,.tsv"
                                    multiple
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, ${COLORS.primary}15 0%, ${COLORS.purple}15 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <ArrowUploadRegular style={{ fontSize: '32px', color: COLORS.primary }} />
                                </div>
                                {selectedFiles.length > 0 ? (
                                    <Stack tokens={{ childrenGap: 8 }} horizontalAlign="center" styles={{ root: { width: '100%' } }}>
                                        <Text variant="medium" style={{ fontWeight: FontWeights.semibold, color: COLORS.success, marginBottom: '8px' }}>
                                            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                                        </Text>
                                        <Stack tokens={{ childrenGap: 6 }} styles={{ root: { width: '100%', maxHeight: '150px', overflowY: 'auto' } }}>
                                            {selectedFiles.map((file, index) => (
                                                <Stack 
                                                    key={index} 
                                                    horizontal 
                                                    tokens={{ childrenGap: 8 }} 
                                                    verticalAlign="center"
                                                    styles={{ 
                                                        root: { 
                                                            padding: '8px 12px', 
                                                            backgroundColor: COLORS.grayLight, 
                                                            borderRadius: '6px',
                                                            justifyContent: 'space-between'
                                                        } 
                                                    }}
                                                >
                                                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                        <DocumentRegular style={{ fontSize: '16px', color: COLORS.primary }} />
                                                        <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                                                            {file.name}
                                                        </Text>
                                                        <Text variant="xSmall" style={{ color: COLORS.gray }}>
                                                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                                        </Text>
                                                    </Stack>
                                                    <IconButton
                                                        iconProps={{ iconName: 'Cancel' }}
                                                        title="Remove file"
                                                        onClick={(e) => { e.stopPropagation(); removeSelectedFile(index); }}
                                                        styles={{ root: { width: '24px', height: '24px' } }}
                                                    />
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <DefaultButton 
                                            text="Add more files" 
                                            iconProps={{ iconName: 'Add' }}
                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                            styles={{ root: { marginTop: '8px', borderRadius: '8px' } }}
                                        />
                                    </Stack>
                                ) : (
                                    <Stack tokens={{ childrenGap: 8 }} horizontalAlign="center">
                                        <Text variant="medium" style={{ color: '#1e293b', fontWeight: FontWeights.semibold }}>
                                            Drag & drop file here
                                        </Text>
                                        <Text variant="small" style={{ color: COLORS.gray }}>
                                            or click to browse
                                        </Text>
                                        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: '8px' } }}>
                                            {['XLSX', 'XLS', 'CSV', 'TSV'].map(ext => (
                                                <span key={ext} style={{
                                                    padding: '4px 10px',
                                                    backgroundColor: COLORS.grayLight,
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: FontWeights.semibold,
                                                    color: COLORS.gray
                                                }}>{ext}</span>
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}
                            </div>
                            
                            {isUploading && (
                                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '16px' } }}>
                                    <Spinner size={SpinnerSize.small} label="Uploading and analyzing..." />
                                    <div style={{ 
                                        height: '6px', 
                                        backgroundColor: COLORS.grayLight, 
                                        borderRadius: '3px', 
                                        overflow: 'hidden' 
                                    }}>
                                        <div style={{ 
                                            width: `${uploadProgress}%`, 
                                            height: '100%', 
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.purple})`,
                                            borderRadius: '3px',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                </Stack>
                            )}
                            
                            <MessageBar 
                                messageBarType={MessageBarType.info}
                                styles={{ root: { marginTop: '16px', borderRadius: '8px' } }}
                            >
                                {selectedFiles.length > 1 
                                    ? `Upload ${selectedFiles.length} files as a single unified data source. Each file's sheets will be available for querying.`
                                    : 'After upload, the AI will analyze the file structure and suggest semantic mappings for each column.'
                                }
                            </MessageBar>
                            
                            <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '20px' } }}>
                                <PrimaryButton 
                                    text={selectedFiles.length > 1 ? `Upload ${selectedFiles.length} Files` : "Upload & Analyze"} 
                                    onClick={handleFileUpload} 
                                    disabled={selectedFiles.length === 0 || !formData.name || isUploading}
                                    styles={{
                                        root: {
                                            borderRadius: '10px',
                                            height: '42px',
                                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                                            border: 'none',
                                            padding: '0 24px'
                                        }
                                    }}
                                />
                                <DefaultButton 
                                    text="Cancel" 
                                    onClick={handleClosePanel}
                                    styles={{ root: { borderRadius: '10px', height: '42px' } }}
                                />
                            </Stack>
                        </div>
                    )}
                    
                    {/* Excel/CSV Edit Mode - show when editing a file source */}
                    {editingSource && (editingSource.source_type === 'excel' || editingSource.source_type === 'csv') && (
                        <div className={styles.formSection}>
                            <div className={styles.formSectionTitle}>
                                <DocumentTableRegular style={{ fontSize: '20px', color: COLORS.purple }} />
                                <span>File Source Details</span>
                            </div>
                            
                            <MessageBar 
                                messageBarType={MessageBarType.info}
                                styles={{ root: { marginBottom: '16px', borderRadius: '8px' } }}
                            >
                                This is a file-based data source. The file has been analyzed and semantic mappings are stored.
                            </MessageBar>
                            
                            <TextField
                                label="Source Type"
                                value={editingSource.source_type.toUpperCase()}
                                disabled
                                styles={{ root: { marginBottom: '12px' } }}
                            />
                            
                            <TextField
                                label="Tables/Sheets"
                                value={`${editingSource.tables_count || 0} sheet(s)`}
                                disabled
                                styles={{ root: { marginBottom: '12px' } }}
                            />
                            
                            <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '20px' } }}>
                                <PrimaryButton 
                                    text="Save Changes" 
                                    onClick={handleSave} 
                                    styles={{
                                        root: {
                                            borderRadius: '10px',
                                            height: '42px',
                                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                                            border: 'none',
                                            padding: '0 24px'
                                        }
                                    }}
                                />
                                <DefaultButton 
                                    text="Cancel" 
                                    onClick={handleClosePanel}
                                    styles={{ root: { borderRadius: '10px', height: '42px' } }}
                                />
                            </Stack>
                        </div>
                    )}
                    
                    {/* SQL Connection Mode - show for SQL panel mode or when editing a SQL source */}
                    {(panelMode === 'sql' && (!editingSource || (editingSource.source_type !== 'excel' && editingSource.source_type !== 'csv'))) && (
                        <>
                            <div className={styles.formSection}>
                                <div className={styles.formSectionTitle}>
                                    <CloudDatabaseRegular style={{ fontSize: '20px', color: COLORS.primary }} />
                                    <span>Database Connection</span>
                                </div>
                                
                                <Dropdown
                                    label="Database Type"
                                    required
                                    selectedKey={formData.source_type}
                                    options={sourceTypeOptions.length > 0 ? sourceTypeOptions : [
                                        { key: 'azure_sql', text: 'Azure SQL' },
                                        { key: 'sql_server', text: 'SQL Server' }
                                    ]}
                                    onChange={(_, option) => setFormData({ ...formData, source_type: option?.key as string || 'azure_sql' })}
                                    styles={{ root: { marginBottom: '12px' } }}
                                />
                            
                                <TextField
                                    label="Server"
                                    required
                                    value={formData.server}
                                    onChange={(_, val) => setFormData({ ...formData, server: val || '' })}
                                    placeholder="e.g., myserver.database.windows.net"
                                    styles={{ root: { marginBottom: '12px' } }}
                                />
                                
                                <Stack horizontal tokens={{ childrenGap: 12 }}>
                                    <Stack.Item grow={2}>
                                        <TextField
                                            label="Database"
                                            required
                                            value={formData.database}
                                            onChange={(_, val) => setFormData({ ...formData, database: val || '' })}
                                            placeholder="e.g., SalesDB"
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <SpinButton
                                            label="Port"
                                            value={formData.port.toString()}
                                            min={1}
                                            max={65535}
                                            step={1}
                                            onChange={(_, val) => setFormData({ ...formData, port: parseInt(val || '1433') })}
                                        />
                                    </Stack.Item>
                                </Stack>
                            </div>
                            
                            <div className={styles.formSection}>
                                <div className={styles.formSectionTitle}>
                                    <PlugConnectedSettingsRegular style={{ fontSize: '20px', color: COLORS.warning }} />
                                    <span>Authentication</span>
                                </div>
                                
                                <ChoiceGroup
                                    selectedKey={formData.auth_type}
                                    options={authTypeOptions}
                                    onChange={(_, option) => setFormData({ ...formData, auth_type: option?.key || 'sql_auth' })}
                                />
                    
                                {formData.auth_type === 'sql_auth' && (
                                    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '16px' } }}>
                                        <TextField
                                            label="Username"
                                            required
                                            value={formData.username}
                                            onChange={(_, val) => setFormData({ ...formData, username: val || '' })}
                                        />
                                        
                                        <TextField
                                            label="Password"
                                            type="password"
                                            required={!editingSource}
                                            value={formData.password}
                                            onChange={(_, val) => setFormData({ ...formData, password: val || '' })}
                                            placeholder={editingSource ? 'Leave blank to keep existing' : ''}
                                            autoComplete="off"
                                            data-lpignore="true"
                                            data-form-type="other"
                                            canRevealPassword={false}
                                        />
                                    </Stack>
                                )}
                                
                                {formData.auth_type === 'managed_identity' && (
                                    <MessageBar 
                                        messageBarType={MessageBarType.info}
                                        styles={{ root: { marginTop: '16px', borderRadius: '8px' } }}
                                    >
                                        Managed Identity will be used to authenticate. Ensure the app has appropriate database access.
                                    </MessageBar>
                                )}
                                
                                {formData.auth_type === 'service_principal' && (
                                    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '16px' } }}>
                                        <MessageBar 
                                            messageBarType={MessageBarType.info}
                                            styles={{ root: { borderRadius: '8px' } }}
                                        >
                                            Use an Azure App Registration to authenticate. The SQL database must grant access to this app.
                                        </MessageBar>
                                        <TextField
                                            label="Tenant ID"
                                            required
                                            value={formData.tenant_id}
                                            onChange={(_, val) => setFormData({ ...formData, tenant_id: val || '' })}
                                            placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                                        />
                                        <TextField
                                            label="Client ID (App ID)"
                                            required
                                            value={formData.client_id}
                                            onChange={(_, val) => setFormData({ ...formData, client_id: val || '' })}
                                            placeholder="e.g., 87654321-4321-4321-4321-cba987654321"
                                        />
                                        <TextField
                                            label="Client Secret"
                                            type="password"
                                            required={!editingSource}
                                            value={formData.client_secret}
                                            onChange={(_, val) => setFormData({ ...formData, client_secret: val || '' })}
                                            placeholder={editingSource ? 'Leave blank to keep existing' : ''}
                                            autoComplete="off"
                                            data-lpignore="true"
                                            data-form-type="other"
                                            canRevealPassword={false}
                                        />
                                    </Stack>
                                )}
                            </div>
                            
                            {/* Connection Test */}
                            <div className={styles.formSection}>
                                <div className={styles.formSectionTitle}>
                                    <PlugConnectedRegular style={{ fontSize: '20px', color: COLORS.success }} />
                                    <span>Test Connection</span>
                                </div>
                                
                                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                                    <DefaultButton
                                        styles={{
                                            root: { borderRadius: '10px', height: '42px' }
                                        }}
                                        onClick={handleTestConnection}
                                        disabled={testingConnection}
                                    >
                                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                            <PlugConnectedRegular style={{ fontSize: '16px' }} />
                                            <span>Test Connection</span>
                                        </Stack>
                                    </DefaultButton>
                                    {testingConnection && <Spinner size={SpinnerSize.small} />}
                                </Stack>
                                
                                {connectionTestResult && (
                                    <MessageBar 
                                        messageBarType={connectionTestResult.success ? MessageBarType.success : MessageBarType.error}
                                        styles={{ root: { marginTop: '12px', borderRadius: '8px' } }}
                                    >
                                        {connectionTestResult.message}
                                    </MessageBar>
                                )}
                            </div>
                            
                            {/* Actions */}
                            <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: '8px' } }}>
                                <PrimaryButton 
                                    onClick={handleSave}
                                    styles={{
                                        root: {
                                            borderRadius: '10px',
                                            height: '44px',
                                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                                            border: 'none',
                                            padding: '0 32px'
                                        }
                                    }}
                                >
                                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                        <CheckmarkCircleRegular style={{ fontSize: '18px' }} />
                                        <span>Save Data Source</span>
                                    </Stack>
                                </PrimaryButton>
                                <DefaultButton 
                                    text="Cancel" 
                                    onClick={handleClosePanel}
                                    styles={{ root: { borderRadius: '10px', height: '44px' } }}
                                />
                            </Stack>
                        </>
                    )}
                </Stack>
            </Panel>
            
            {/* Semantic Mapping Panel */}
            {selectedSourceForMapping && (
                <Panel
                    isOpen={isMappingPanelOpen}
                    onDismiss={() => setIsMappingPanelOpen(false)}
                    type={PanelType.extraLarge}
                    headerText={`Semantic Mapping: ${selectedSourceForMapping.name}`}
                    closeButtonAriaLabel="Close"
                    styles={{
                        main: { backgroundColor: COLORS.pageBg }
                    }}
                >
                    <Pivot styles={{ root: { marginTop: '16px' } }}>
                        {/* Files tab - only for Excel sources */}
                        {(selectedSourceForMapping.source_type === 'excel' || selectedSourceForMapping.source_type === 'csv') && (
                            <PivotItem headerText="Files" itemIcon="Document">
                                <FilesManager 
                                    sourceId={selectedSourceForMapping.id}
                                    onFilesChanged={() => {
                                        fetchSources();
                                        setSuccess('Files updated successfully');
                                    }}
                                />
                            </PivotItem>
                        )}
                        <PivotItem headerText="Tables & Mappings" itemIcon="Table">
                            <SemanticMappingEditor 
                                sourceId={selectedSourceForMapping.id} 
                                onSave={() => {
                                    fetchSources();
                                    setSuccess('Mappings saved successfully');
                                }}
                            />
                        </PivotItem>
                        <PivotItem headerText="Example Questions" itemIcon="QuestionCircle">
                            <ExampleQuestionsEditor 
                                sourceId={selectedSourceForMapping.id}
                                onSave={() => {
                                    fetchSources();
                                    setSuccess('Example questions saved successfully');
                                }}
                            />
                        </PivotItem>
                        <PivotItem headerText="Relationships" itemIcon="Flow">
                            <RelationshipsEditor
                                sourceId={selectedSourceForMapping.id}
                                onSave={() => {
                                    fetchSources();
                                    setSuccess('Relationships saved successfully');
                                }}
                            />
                        </PivotItem>
                    </Pivot>
                </Panel>
            )}
        </div>
    );
};

export default StructuredDataSources;
